/* =========================================================
   Instructor detail — identity header + horizontal tabs
   (Datos personales · Identificadores · Afiliado a).

   An instructor is a free entity: the affiliations tab lists
   every org+site assignment. Each affiliation carries a
   "Desasociar de este centro" checkbox that raises a request
   for the ETC Admin — Site Managers can neither edit data nor
   raise the request.

   Al marcarla se pide el motivo. No lo pedía, y el Admin ETC
   tenía que aprobar o rechazar a ciegas: un nombre, un site, y
   decide. Es opcional, porque obligarlo llenaría la base de
   «baja» y «ya no trabaja aquí».
   ========================================================= */
import React, { useEffect, useReducer, useState } from 'react';
import type { Instructor } from '@/types';
import { ic, sized } from '@/components/Icons';
import { EmptyState, FieldLegend, FieldRow, Modal, PageHeader } from '@/components/ui';
import { COUNTRIES, ds } from '@/data';
import { useI18n } from '@/i18n';
import { camposDeSeccion, instructorSchema } from '@/lib/fields';
import { useGo } from '@/hooks/useRoute';
import { usePuedeEditar } from '@/hooks/usePuedeEditar';
import { useAppStore } from '@/store/useAppStore';
import { instrInitials } from './InstrAvatar';
import { lineaActualizado } from '@/lib/actualizado';

const TABS = [
  { k: 'personal', tone: 'var(--st-edit)' },
  { k: 'identifiers', tone: 'var(--st-read)' },
  { k: 'assignments', tone: 'var(--st-lock)' },
];

/** El borrador de una ficha en edición.
 *
 *  Los valores son texto salvo los campos «multi» —industria, portafolio,
 *  entorno de formación— que son listas de códigos. */
type Draft = Record<string, string | string[] | undefined>;

export function InstructorDetail({ instructorId }: { instructorId: string }) {
  const { lang, t } = useI18n();
  const go = useGo();
  const role = useAppStore((s) => s.role);
  const addToast = useAppStore((s) => s.addToast);
  const windowState = useAppStore((s) => s.windowState);
  /** La afiliación que se está desasociando, mientras se pide el motivo. */
  const [pidiendo, setPidiendo] = useState<{ SiteId: string; SiteName?: string; OrgId?: string; OrgName?: string } | null>(null);
  const [motivo, setMotivo] = useState('');

  // La desasociación es un cambio del padrón como cualquier otro: fuera de
  // la ventana no se pide. El servidor ya lo rechazaba, pero la casilla se
  // ofrecía igual y el socio veía primero «solicitud enviada» y después el
  // error — dos avisos que se contradicen.
  const puedePedir = role === 'ETC_ADMIN' || windowState.open;
  const permiso = usePuedeEditar('instructor');

  const instr = ds.getInstructor(instructorId);
  const asg = ds.listAssignments({ instructorId });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({ ...(instr as unknown as Draft) });
  const [tab, setTab] = useState('personal');
  const [, forceRefresh] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    setDraft({ ...(ds.getInstructor(instructorId) as unknown as Draft) });
    setEditing(false);
  }, [instructorId]);

  if (!instr) {
    return <EmptyState icon={ic.instr} title={lang === 'es' ? 'Instructor no encontrado' : 'Instructor not found'} />;
  }

  function setField(k: string, v: string | string[]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  /** Entrar o salir de la edición parte siempre de lo guardado: ver
   *  cambiarEdicion en OrgDetail, que tuvo el mismo fallo con LastUpdated. */
  function cambiarEdicion(v: boolean) {
    setDraft({ ...(ds.getInstructor(instructorId) as unknown as Draft) });
    setEditing(v);
  }

  function save() {
    ds.updateInstructor(instructorId, draft as Partial<Instructor>);
    setEditing(false);
    addToast({ kind: 'save', celebrate: true, title: lang === 'es' ? 'Cambios guardados' : 'Changes saved' });
  }

  function toggleDisassoc(a: (typeof asg)[number], checked: boolean) {
    if (checked) {
      // El motivo se pide antes de mandar nada: una vez enviada, la
      // solicitud ya está en la bandeja del Admin ETC.
      setMotivo('');
      setPidiendo({ SiteId: a.SiteId, SiteName: a.SiteName, OrgId: a.OrgId, OrgName: a.OrgName });
      return;
    }
    ds.cancelDisassociation(instructorId, a.SiteId);
    addToast({ kind: 'ok', title: lang === 'es' ? 'Solicitud retirada' : 'Request withdrawn', body: a.SiteName });
    forceRefresh();
  }

  function confirmarDesasociar() {
    const a = pidiendo;
    if (!a) return;
    ds.requestDisassociation({
      InstructorId: instructorId,
      SiteId: a.SiteId,
      OrgId: a.OrgId,
      SiteName: a.SiteName,
      OrgName: a.OrgName,
      reason: motivo.trim() || undefined,
    });
      addToast({
        kind: 'ok',
        title: lang === 'es' ? 'Notificación enviada al Admin ETC' : 'Notification sent to ETC Admin',
        body:
          lang === 'es'
            // Decía «se envió un email a los usuarios administradores». No se
            // envía ninguno: el portal todavía no manda correo. El aviso llega
            // a la campana del Admin ETC, que es lo que de verdad ocurre.
            ? `Se solicitó retirar al instructor de ${a.SiteName}. Al Admin ETC le aparece en su campana para que lo resuelva.`
            : `Removal from ${a.SiteName} was requested. It appears in the ETC Admin's bell for them to resolve.`,
        origin: lang === 'es' ? 'Instructores · Afiliado a' : 'Instructors · Affiliated to',
        details: `${instr!.EnglishFirstName} ${instr!.EnglishLastName} (${instructorId}) — ${a.SiteName} (${a.SiteId}) · ${a.OrgName}`,
        target: { screen: 'instructors', params: { view: 'disassoc' } },
        targetLabel: lang === 'es' ? 'Gestionar solicitudes de desasociación' : 'Manage disassociation requests',
    });
    setPidiendo(null);
    setMotivo('');
    forceRefresh();
  }

  const fullName = `${instr.EnglishFirstName} ${instr.EnglishLastName}`;

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={fullName}
        sub={[instr.JobTitle, lineaActualizado(instr, lang)].filter(Boolean).join(' · ')}
        crumbs={[{ label: t('nav.instructors'), onClick: () => go('instructors') }, { label: fullName }]}
      />

      {/* identity header */}
      <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
          {draft.Photo ? (
            <img
              src={String(draft.Photo)}
              alt=""
              style={{ width: 72, height: 72, borderRadius: 99, objectFit: 'cover', border: '2px solid var(--surface)', boxShadow: 'var(--shadow-2)' }}
            />
          ) : (
            <div
              style={{
                width: 72, height: 72, borderRadius: 99, background: 'linear-gradient(140deg,var(--brand-primary-l),#fff)',
                color: 'var(--brand-primary-d)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 26, border: '2px solid var(--surface)', boxShadow: 'var(--shadow-2)',
              }}
            >
              {instrInitials(instr)}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{fullName}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{instr.JobTitle}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {instr.disassociated ? (
              <span className="chip warn">{ic.warn} {t('instr.filter_orphan')}</span>
            ) : (
              <span className="chip ok">{ic.check} {t('common.active')}</span>
            )}
            <span className="chip">{instr.Certification}</span>
            <span className="chip">{instr.AccessRoles}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {editing ? (
            <>
              <button className="btn" onClick={() => cambiarEdicion(false)}>{t('common.cancel')}</button>
              <button className="btn primary" onClick={save}>{ic.save} {t('common.save')}</button>
            </>
          ) : !permiso.permanente ? (
            // Fuera de la ventana el servidor rechaza el guardado. Dejar el
            // botón vivo hacía llenar la ficha entera para perderla al
            // guardar; ahora el botón lo dice antes de empezar.
            <button
              className="btn primary"
              disabled={!permiso.puede}
              data-tip={permiso.motivo || undefined}
              onClick={() => cambiarEdicion(true)}
            >
              {ic.edit} {t('common.edit')}
            </button>
          ) : (
            <span className="chip lock" data-tip={permiso.motivo || undefined}>
              {ic.eye} {lang === 'es' ? 'Solo lectura' : 'View only'}
            </span>
          )}
        </div>
      </div>

      {/* tabs */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--line)', overflowX: 'auto', background: 'var(--surface-2)' }}>
          {TABS.map((s) => (
            <button
              key={s.k}
              role="tab"
              aria-selected={tab === s.k}
              onClick={() => setTab(s.k)}
              className="btn ghost tab"
              style={{
                padding: '12px 16px', borderRadius: 0, border: 'none',
                borderBottom: tab === s.k ? '2px solid var(--brand-primary)' : '2px solid transparent',
                color: tab === s.k ? 'var(--brand-primary-d)' : 'var(--text-2)',
                fontWeight: tab === s.k ? 700 : 500, fontSize: 13, gap: 8, background: 'transparent', whiteSpace: 'nowrap',
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 99, background: s.tone, flexShrink: 0 }} />
              {t('sec.' + s.k)}
              {s.k === 'assignments' ? <span className="chip" style={{ padding: '1px 6px', fontSize: 9 }}>{asg.length}</span> : null}
            </button>
          ))}
        </div>

        <div style={{ padding: 18 }}>
          {tab === 'assignments' ? (
            asg.length === 0 ? (
              <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                {ic.warn} {lang === 'es' ? 'Sin afiliaciones activas — instructor desasociado' : 'No active affiliations — disassociated instructor'}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8 }}>
                {asg.map((a, i) => {
                  const pending = ds.hasDisassocRequest(instructorId, a.SiteId);
                  return (
                    <div
                      key={i}
                      style={{
                        background: 'var(--surface)', padding: '10px 12px', borderRadius: '0 6px 6px 0',
                        border: '1px solid var(--line)', borderLeft: '3px solid ' + (pending ? 'var(--err)' : 'var(--st-lock)'),
                        display: 'grid', gridTemplateColumns: '1fr auto', gap: 4,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{a.SiteName}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{a.OrgName}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 11, color: 'var(--text-3)', flexWrap: 'wrap' }}>
                          <span className="mono">{a.OrgId}</span>
                          <span>·</span>
                          <span className="mono">{a.SiteId}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                        <span className="chip lock">{a.PrimaryRoleId}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {COUNTRIES.find((c) => c.code === a.SiteCountry)?.[lang === 'es' ? 'name_es' : 'name_en']}
                        </span>
                      </div>

                      {!permiso.permanente ? (
                        <label
                          style={{
                            gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8,
                            marginTop: 8, paddingTop: 9, borderTop: '1px dashed var(--line)',
                            fontSize: 12, cursor: 'pointer', color: pending ? 'var(--err)' : 'var(--text-2)',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={pending}
                            disabled={!puedePedir && !pending}
                            onChange={(e) => toggleDisassoc(a, e.target.checked)}
                            style={{ accentColor: 'var(--err)', width: 15, height: 15, cursor: puedePedir ? 'pointer' : 'not-allowed', flexShrink: 0, margin: 0 }}
                          />
                          <span style={{ fontWeight: 600 }}>{lang === 'es' ? 'Desasociar de este centro' : 'Disassociate from this center'}</span>
                          {pending ? (
                            <span className="chip warn" style={{ marginLeft: 'auto', fontSize: 9 }}>
                              {lang === 'es' ? 'Solicitud enviada' : 'Request sent'}
                            </span>
                          ) : !puedePedir ? (
                            <span className="chip lock" style={{ marginLeft: 'auto', fontSize: 9 }}>
                              {lang === 'es' ? 'Proceso cerrado' : 'Process closed'}
                            </span>
                          ) : null}
                        </label>
                      ) : pending ? (
                        <div
                          style={{
                            gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8,
                            marginTop: 8, paddingTop: 9, borderTop: '1px dashed var(--line)', fontSize: 12, color: 'var(--text-3)',
                          }}
                        >
                          {sized(ic.warn, 13)}
                          <span>{lang === 'es' ? 'Solicitud de desasociación pendiente' : 'Disassociation request pending'}</span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <>
              <FieldLegend />
              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1 }}>
                {camposDeSeccion(instructorSchema, tab).map((f) => (
                  <FieldRow
                    key={f.key}
                    field={f}
                    value={draft[f.key]}
                    onChange={setField}
                    editing={tab === 'personal' ? editing : false}
                    entidad="instructor"
                    id={instructorId}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        open={!!pidiendo}
        onClose={() => setPidiendo(null)}
        width={520}
        title={lang === 'es' ? 'Solicitar la desasociación' : 'Request disassociation'}
        footer={
          <>
            <button className="btn" onClick={() => setPidiendo(null)}>{t('common.cancel')}</button>
            <button className="btn danger" onClick={confirmarDesasociar}>
              {lang === 'es' ? 'Enviar solicitud' : 'Send request'}
            </button>
          </>
        }
      >
        <p style={{ marginTop: 0, fontSize: 13, color: 'var(--text-2)' }}>
          {lang === 'es'
            ? <>Se pedirá retirar a <strong>{instr?.EnglishFirstName} {instr?.EnglishLastName}</strong> de <strong>{pidiendo?.SiteName}</strong>. Lo resuelve el Admin ETC.</>
            : <>You are requesting the removal of <strong>{instr?.EnglishFirstName} {instr?.EnglishLastName}</strong> from <strong>{pidiendo?.SiteName}</strong>. The ETC Admin resolves it.</>}
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
            {lang === 'es' ? 'Motivo (opcional)' : 'Reason (optional)'}
          </span>
          <textarea
            rows={3}
            autoFocus
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={1000}
            placeholder={lang === 'es'
              ? 'Por ejemplo: dejó de impartir cursos en este centro en agosto.'
              : 'For example: stopped teaching at this centre in August.'}
            style={{
              padding: '9px 11px', borderRadius: 6, fontSize: 13, resize: 'vertical',
              border: '1px solid var(--line-2)', background: 'var(--bg-1)', color: 'inherit',
            }}
          />
        </label>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 6 }}>
          {lang === 'es'
            ? 'El Admin ETC lo verá al decidir. Sin motivo la solicitud se manda igual, pero tendrá que resolverla sin saber por qué.'
            : 'The ETC Admin sees it when deciding. Without a reason the request still goes through, but they will have to resolve it without knowing why.'}
        </div>
      </Modal>
    </div>
  );
}

export default React.memo(InstructorDetail);
